import { useState } from 'react'
import { format, subDays } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import Layout from '../../components/Layout'
import DateRangePicker from '../../components/DateRangePicker'
import MetricCard from '../../components/MetricCard'
import { useAdminAnalytics } from '../../api/admin'

export default function Analytics() {
  const navigate = useNavigate()
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 29), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  })
  const [sortField, setSortField] = useState<'username' | 'requests' | 'cost_usd' | 'approval_rate' | 'last_active'>('requests')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  const { data, isLoading, error } = useAdminAnalytics(dateRange.start, dateRange.end)

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  const sortedUsers = data?.users ? [...data.users].sort((a, b) => {
    let aValue: string | number = a[sortField]
    let bValue: string | number = b[sortField]

    // Handle string vs number comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue)
    }

    // Handle numeric comparison
    const aNum = typeof aValue === 'number' ? aValue : 0
    const bNum = typeof bValue === 'number' ? bValue : 0

    return sortDirection === 'asc' ? aNum - bNum : bNum - aNum
  }) : []

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div
              className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-accent-cyan border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"
              role="status"
              aria-label="Loading analytics"
            >
              <span className="sr-only">Loading analytics...</span>
            </div>
            <p className="mt-4 text-sm text-text-secondary">Loading analytics...</p>
          </div>
        </div>
      </Layout>
    )
  }

  if (error) {
    return (
      <Layout>
        <div className="rounded-md bg-verdict-rejected/10 border border-verdict-rejected/30 p-6">
          <div className="flex">
            <div className="shrink-0">
              <svg
                className="h-5 w-5 text-verdict-rejected"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-verdict-rejected">Error loading analytics</h3>
              <p className="mt-2 text-sm text-verdict-rejected">
                {error instanceof Error ? error.message : 'Failed to load analytics'}
              </p>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  const summary = data?.summary

  const SortIcon = ({ field }: { field: typeof sortField }) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      )
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-accent-cyan" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-accent-cyan" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    )
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold text-text-primary">Admin Analytics</h1>
          <DateRangePicker
            startDate={dateRange.start}
            endDate={dateRange.end}
            onChange={(start, end) => setDateRange({ start, end })}
          />
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <MetricCard
            title="Total Users"
            value={summary?.total_users?.toLocaleString() || '0'}
          />
          <MetricCard
            title="Active Users"
            value={summary?.active_users?.toLocaleString() || '0'}
          />
          <MetricCard
            title="Total Requests"
            value={summary?.total_requests?.toLocaleString() || '0'}
          />
          <MetricCard
            title="Total Cost"
            value={`$${(summary?.total_cost_usd || 0).toFixed(2)}`}
          />
          <MetricCard
            title="Approval Rate"
            value={`${((summary?.approval_rate || 0) * 100).toFixed(1)}%`}
          />
        </div>

        {/* User Analytics Table */}
        <div className="bg-noir-secondary border border-border rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-lg font-medium text-text-primary">User Breakdown</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-noir-tertiary">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-noir-tertiary transition-colors"
                    onClick={() => handleSort('username')}
                  >
                    <div className="flex items-center gap-1">
                      Username
                      <SortIcon field="username" />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-noir-tertiary transition-colors"
                    onClick={() => handleSort('requests')}
                  >
                    <div className="flex items-center gap-1">
                      Requests
                      <SortIcon field="requests" />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-noir-tertiary transition-colors"
                    onClick={() => handleSort('cost_usd')}
                  >
                    <div className="flex items-center gap-1">
                      Cost (USD)
                      <SortIcon field="cost_usd" />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-noir-tertiary transition-colors"
                    onClick={() => handleSort('approval_rate')}
                  >
                    <div className="flex items-center gap-1">
                      Approval Rate
                      <SortIcon field="approval_rate" />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:bg-noir-tertiary transition-colors"
                    onClick={() => handleSort('last_active')}
                  >
                    <div className="flex items-center gap-1">
                      Last Active
                      <SortIcon field="last_active" />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sortedUsers.map((user) => (
                  <tr
                    key={user.user_id}
                    className="hover:bg-noir-tertiary transition-colors cursor-pointer"
                    onClick={() => navigate(`/admin/analytics/${user.user_id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-text-primary">
                        {user.username}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-text-primary">
                        {user.requests.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-text-primary">
                        ${user.cost_usd.toFixed(4)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-text-primary">
                        {(user.approval_rate * 100).toFixed(1)}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-text-secondary">
                      {user.last_active ? format(new Date(user.last_active), 'MMM d, yyyy') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {sortedUsers.length === 0 && (
              <div className="text-center py-12">
                <svg
                  className="mx-auto h-12 w-12 text-text-secondary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-text-primary">No data available</h3>
                <p className="mt-1 text-sm text-text-secondary">
                  No user activity found for the selected date range.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  )
}
